import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/helpers';

const FeederCatCard = ({ cat, onEdit, onDelete, onDetails }) => {
  const navigate = useNavigate();
  const defaultImage = '/cat-placeholder.png';
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photos, setPhotos] = useState([]);
  
  // Log cat data to inspect structure
  console.log('Cat data in FeederCatCard:', cat);
  
  // Process photo data on component mount or when cat changes
  useEffect(() => {
    // Set up photos array based on the response structure
    let photosArray = [];
    
    // First, check if catPhotos array of objects is present (from /cat/:id endpoint)
    if (cat.catPhotos && cat.catPhotos.length > 0) {
      // Extract paths from the catPhotos objects
      photosArray = cat.catPhotos.map(photo => photo.path);
      console.log('Using catPhotos object array, extracted paths:', photosArray);
    }
    // Next, check for catPhotoPaths array of strings (from /feeder/cats endpoint)
    else if (cat.catPhotoPaths && cat.catPhotoPaths.length > 0) {
      photosArray = cat.catPhotoPaths;
      console.log('Using catPhotoPaths array of URLs:', photosArray);
    }
    // Then, fall back to mainPhoto if available
    else if (cat.mainPhoto) {
      photosArray = [cat.mainPhoto];
      console.log('Using mainPhoto as fallback');
    }
    // Finally, use the default placeholder
    else {
      photosArray = [defaultImage];
      console.log('No photos found, using default placeholder');
    }
    
    setPhotos(photosArray);
  }, [cat, defaultImage]);
  
  // Format the last feeding date or show "Not yet fed" if it's null
  const formattedLastFed = cat.lastFed 
    ? formatDate(cat.lastFed, { hour: '2-digit', minute: '2-digit' }) 
    : 'Not yet fed';
    
  // Handle feed button click
  const handleFeedClick = () => {
    navigate(`/feeder/create-feeding/${cat.id}`);
  };
  
  // Navigate to previous photo
  const prevPhoto = (e) => {
    e.stopPropagation();
    if (photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex - 1 + photos.length) % photos.length
    );
  };
  
  // Navigate to next photo
  const nextPhoto = (e) => {
    e.stopPropagation();
    if (photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex + 1) % photos.length
    );
  };
  
  // Get current photo URL
  const getCurrentPhotoUrl = () => {
    if (photos.length === 0) return defaultImage;
    return photos[currentPhotoIndex] || defaultImage;
  };
  
  // Determine if navigation controls should be shown
  const showNavigation = photos.length > 1;
  
  // Handle edit button click
  const handleEditClick = () => {
    if (onEdit) {
      onEdit(cat);
    } else {
      navigate(`/feeder/edit-cat/${cat.id}`);
    }
  };
  
  // Handle delete button click
  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(cat);
    }
  };
  
  // Handle details button click
  const handleDetailsClick = () => {
    if (onDetails) {
      onDetails(cat);
    } else {
      navigate(`/feeder/cat/${cat.id}`);
    }
  };
  
  return (
    <div className="feeder-cat-card">
      <div className="feeder-cat-header">
        <h3 className="feeder-cat-name">{cat.name}</h3>
        <div className="feeder-cat-gender">
          {cat.gender === 'male' ? '♂️' : '♀️'}
        </div>
      </div>
      
      <div className="feeder-cat-image">
        <img 
          src={getCurrentPhotoUrl()} 
          alt={cat.name} 
          onError={(e) => {
            console.log('Image failed to load, using fallback');
            e.target.src = defaultImage;
          }}
        />
        
        {showNavigation && (
          <>
            <button 
              className="photo-nav-button prev-button" 
              onClick={prevPhoto}
              aria-label="Previous photo"
            >
              ‹
            </button>
            <button 
              className="photo-nav-button next-button" 
              onClick={nextPhoto}
              aria-label="Next photo"
            >
              ›
            </button>
            <div className="photo-indicators">
              {photos.map((_, index) => (
                <span 
                  key={index} 
                  className={`photo-indicator ${index === currentPhotoIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPhotoIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
      
      <div className="feeder-cat-info">
        <p className="feeder-cat-description">
          {cat.description?.length > 80 
            ? `${cat.description.substring(0, 80)}...` 
            : cat.description || 'No description available'}
        </p>
        
        <div className="feeder-cat-meta">
          <span>Last Fed: {formattedLastFed}</span>
          <span>Pending Requests: {cat.pendingRequests || cat.nonProcessedFeedingRequestsCount || 0}</span>
        </div>
      </div>
      
      <div className="feeder-cat-actions">
        <button 
          className="button"
          onClick={handleFeedClick}
        >
          Feed Now
        </button>
        
        <button 
          className="button secondary"
          onClick={handleDetailsClick}
        >
          Details
        </button>
        
        {onEdit && (
          <button 
            className="button secondary"
            onClick={handleEditClick}
          >
            Edit
          </button>
        )}
        
        {onDelete && (
          <button 
            className="button danger"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default FeederCatCard; 